import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReportActionDialog from "./ReportActionDialog";
import { Dropdown, TextInput } from "../../../common_components/FormInputs";
import { toast } from "../../../common_components/Toast/ToastUtils";
import { selectToken } from "../../../redux/authSlice";
import { ROUTES } from "../../../routes/routes.constants";
import reportBuilderService from "../../../services/reportBuilder.service";
const CloneReportModal = ({ open, clientOptions, defaultClientId, defaultReportName, clientsLoading = false, loading = false, nameErrorMessage, checkNetworks, onConfirm, onClose, }) => {
    const [networkCheck, setNetworkCheck] = useState(null);
    const [clientId, setClientId] = useState(defaultClientId);
    const [reportName, setReportName] = useState(defaultReportName);
    const [showValidation, setShowValidation] = useState(false);
    // Every open starts from the current report again, so a cancelled attempt
    // never leaks its half-typed name into the next one.
    useEffect(() => {
        if (open) {
            setClientId(defaultClientId);
            setReportName(defaultReportName);
            setShowValidation(false);
        }
        // Defaults are read only at open time.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    // The warning follows the picked client: asked again on every change.
    useEffect(() => {
        setNetworkCheck(null);
        if (!open || !clientId || !checkNetworks)
            return;
        let cancelled = false;
        checkNetworks(clientId).then((result) => {
            if (!cancelled)
                setNetworkCheck(result);
        });
        return () => {
            cancelled = true;
        };
        // checkNetworks is rebuilt every render; the client and open drive this.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, clientId]);
    const trimmedName = reportName.trim();
    const nameError = (showValidation && !trimmedName) || Boolean(nameErrorMessage);
    const clientError = showValidation && !clientId;
    const handleConfirm = () => {
        if (!trimmedName || !clientId) {
            setShowValidation(true);
            return;
        }
        onConfirm({
            client_id: clientId,
            client_name: clientOptions.find((opt) => opt.value === clientId)?.label || "",
            report_name: trimmedName,
        });
    };
    return (_jsxs(ReportActionDialog, { open: open, title: "Clone Report", confirmText: "Clone & edit", loading: loading, confirmDisabled: Boolean(networkCheck?.allMissing), onConfirm: handleConfirm, onClose: onClose, children: [_jsx(TextInput, { label: "New report name", required: true, value: reportName, onChange: (e) => setReportName(e.target.value), placeholder: "Enter report name", fullWidth: true, disabled: loading, error: nameError, errorMessage: nameErrorMessage || (nameError ? "Report name is required." : undefined) }), _jsx(Dropdown, { label: "Client", required: true, value: clientId, options: clientOptions, onChange: (value) => setClientId(value), placeholder: "Select Client", searchable: true, fullWidth: true, loading: clientsLoading, disabled: loading, error: clientError, errorMessage: clientError ? "Select the client for this copy." : undefined, 
                // The dialog clips its own overflow, so the menu is portalled out.
                usePortal: true }), networkCheck?.allMissing ? (_jsxs(Typography, { sx: { fontSize: "14px !important", color: "#B42318", lineHeight: 1.5 }, children: ["No active account is connected for this client on any of the report's networks (", networkCheck.missing.join(", "), "), so the report cannot be cloned."] })) : networkCheck && networkCheck.missing.length > 0 ? (_jsxs(Typography, { sx: { fontSize: "14px !important", color: "#B54708", lineHeight: 1.5 }, children: ["No active account is connected for this client on ", networkCheck.missing.join(", "), ". The copy will be created without the metrics of ", networkCheck.missing.length > 1 ? "these networks" : "this network", "."] })) : null] }));
};
export default CloneReportModal;
/**
 * Clone flow shared by the listing and the Edit page: open the dialog for a
 * saved report, POST the copy, then open the copy on Edit - where any network
 * the new client has no account on is named above the report.
 */
export const useCloneReport = () => {
    const token = useSelector(selectToken);
    const navigate = useNavigate();
    const [target, setTarget] = useState(null);
    const [cloning, setCloning] = useState(false);
    const [nameErrorMessage, setNameErrorMessage] = useState("");
    const open = (next) => {
        setNameErrorMessage("");
        setTarget(next);
    };
    const close = () => {
        if (!cloning)
            setTarget(null);
    };
    /**
     * POST the copy and open it on Edit. Shared by the dialog and the G8A AI
     * Agent's clone card, which has no dialog: a name the API refuses comes
     * back as `nameError` so each caller can show it beside its own field.
     */
    const cloneNow = async (source, submission) => {
        setCloning(true);
        try {
            const res = await reportBuilderService.cloneReport(source.report_id, { client_id: submission.client_id, report_name: submission.report_name }, token);
            const body = res?.data?.success ? res.data : res?.response?.data ?? res?.data;
            if (!body?.success) {
                if (body?.field === "report_name")
                    return { ok: false, nameError: body.message };
                throw new Error(body?.message);
            }
            // The API numbers a name the client already has ("Name (1)").
            const clonedName = body.data?.report_name || submission.report_name;
            toast.success(`Cloned as "${clonedName}"${submission.client_name ? ` for ${submission.client_name}` : ""}.`);
            navigate(ROUTES.createReportEdit.path.replace(":reportId", body.data.report_id));
            return { ok: true };
        }
        catch (err) {
            toast.error(err?.message || "Could not clone the report. Please try again.");
            return { ok: false };
        }
        finally {
            setCloning(false);
        }
    };
    /** Asks the API which of the report's networks `clientId` has no account on. */
    const checkNetworksFor = async (reportId, clientId) => {
        if (!reportId)
            return null;
        try {
            const res = await reportBuilderService.cloneReport(reportId, { client_id: clientId, check_only: true }, token);
            const body = res?.data?.success ? res.data : res?.response?.data ?? res?.data;
            if (!body?.success)
                return null;
            return {
                missing: (body.data?.unavailable_networks || []).map((entry) => entry.label),
                allMissing: Boolean(body.data?.all_unavailable),
            };
        }
        catch {
            // No warning if the check fails - the clone itself still refuses a
            // client with none of the networks.
            return null;
        }
    };
    const checkNetworks = (clientId) => checkNetworksFor(target?.report_id || "", clientId);
    const confirm = async (submission) => {
        if (!target)
            return;
        setNameErrorMessage("");
        const result = await cloneNow(target, submission);
        if (result.nameError)
            setNameErrorMessage(result.nameError);
        if (result.ok)
            setTarget(null);
    };
    return {
        open,
        cloneNow,
        checkNetworksFor,
        cloning,
        modalProps: {
            open: Boolean(target),
            defaultClientId: target?.client_id || "",
            defaultReportName: target ? `${target.report_name.trim()} (Copy)` : "",
            loading: cloning,
            nameErrorMessage,
            checkNetworks,
            onConfirm: confirm,
            onClose: close,
        },
    };
};
